import React, { useEffect, useRef } from 'react';
import { Draggable } from '@hello-pangea/dnd';

// Design System Components
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { ArrowRight, Drag_n_drop } from 'Components/DesignSystem/Iconography/Icons';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';

// Constants
import { STRINGS, railDraggableId } from '../../../constants';

// Types
import { FieldDefinition } from '../../../types';

interface FieldRowProps {
  definition: FieldDefinition;
  index: number;
  isEnabled: boolean;
  /** True while this field's properties panel is the open one (spec R79). */
  isSelected: boolean;
  /** True when the panel just closed and this row should take focus back. */
  shouldReturnFocus: boolean;
  onToggle: (enabled: boolean) => void;
  onOpenProperties: () => void;
  onFocusReturned: () => void;
}

/**
 * One row of the configuration pane: the six-dot grip that reorders the field, the
 * **checkbox** that adds or removes its block, and — for every enabled field — the
 * chevron that opens its properties panel (spec R37, R41, R42, R46, R47, R60).
 *
 * The enablement control is a checkbox, never a switch (spec R60), and it comes
 * from the design system as a native input so the row is operable by keyboard
 * (NFR A1). Clicking anywhere else on the row ticks it too, which is what the
 * prototype does; the grip, the checkbox's own label and the chevron each stop
 * their click so the row handler cannot double-fire against them.
 *
 * Only the grip carries the drag handle. It is focusable and driven by
 * `react-beautiful-dnd`'s keyboard sensor, so the reorder completes without a
 * pointer (spec R10).
 *
 * A field that does not support reordering (`supportsReorder: false` — today only
 * the Primary Image) gets no grip and its `Draggable` is disabled, so the library
 * asks for no drag handle. A same-size spacer stands in its place: the grip is
 * 20px of a shared left column, and dropping it would pull that one row's checkbox
 * out of line with every other. The row is otherwise unchanged — it still ticks,
 * still opens its properties panel, and other rows still reorder around it.
 *
 * Chrome: a bordered, rounded row that takes the accent border and the pale accent
 * fill while its properties panel is the open one, as this panel family draws a
 * selected row (spec R79, R80). The state is the editor's one inspector target, so a
 * row can never look selected while a different element's panel is open — and the
 * highlight is the same fact as the outline on that element's block in the preview.
 */
const FieldRow = ({
  definition,
  index,
  isEnabled,
  isSelected,
  shouldReturnFocus,
  onToggle,
  onOpenProperties,
  onFocusReturned,
}: FieldRowProps) => {
  const propertiesRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (shouldReturnFocus) {
      propertiesRef.current?.focus();
      onFocusReturned();
    }
  }, [shouldReturnFocus, onFocusReturned]);

  const stopClick = (event: React.MouseEvent<HTMLElement>) => event.stopPropagation();

  const canReorder = definition.supportsReorder;

  return (
    <Draggable
      draggableId={railDraggableId(definition.id)}
      index={index}
      isDragDisabled={!canReorder}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={() => onToggle(!isEnabled)}
          className={`tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-mb-1 tw-rounded tw-border tw-cursor-pointer tw-transition-colors ${
            isSelected
              ? 'tw-border-accent tw-bg-accent-tint'
              : 'tw-bg-surface tw-border-edge hover:tw-border-accent hover:tw-bg-surface-hover'
          } ${snapshot.isDragging ? 'tw-shadow-ds-md' : ''}`}
        >
          {canReorder ? (
            <span
              {...provided.dragHandleProps}
              onClick={stopClick}
              aria-label={`${STRINGS.pane.reorderHandle}: ${definition.name}`}
              className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-shrink-0 tw-rounded-sm tw-text-ink-tertiary tw-cursor-grab hover:tw-text-ink focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accent"
            >
              <Drag_n_drop />
            </span>
          ) : (
            /* Holds the grip's column open — see the docblock. Decorative, and
               named by nothing: there is no control here to announce. */
            <span aria-hidden="true" className="tw-w-5 tw-h-5 tw-shrink-0" />
          )}

          <span className="tw-flex-1 tw-min-w-0" onClick={stopClick}>
            <Checkbox
              label={definition.name}
              checked={isEnabled}
              onChange={onToggle}
              size="sm"
            />
          </span>

          {isEnabled && (
            <IconButton
              variant="subtle"
              size="sm"
              ref={propertiesRef}
              onClick={(event) => {
                stopClick(event);
                onOpenProperties();
              }}
              aria-label={`${STRINGS.pane.openProperties}: ${definition.name}`}
              title={STRINGS.pane.openProperties}
            >
              <ArrowRight size={16} aria-hidden="true" />
            </IconButton>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default FieldRow;
